<template>
  <v-container fluid class="pa-4">
    <v-card class="elevation-2">
      <v-toolbar color="amber darken-3" dark dense>
        <v-icon left>event_busy</v-icon>
        <v-toolbar-title>Banka Tatilleri</v-toolbar-title>
        <v-spacer />
        <v-btn small color="white" class="amber--text text--darken-3" @click="openCreate">
          <v-icon left small>add</v-icon>
          Yeni Tatil
        </v-btn>
      </v-toolbar>

      <v-card-text>
        <v-alert :value="true" type="info" outline dense class="mb-3">
          Milli bayramlar yıllık tekrarlı işaretlenmelidir (sadece Ay/Gün dikkate alınır, yıl yok sayılır — bir kez eklenir, sonsuza kadar geçerli). Dini bayramlar ve özel tatiller yıllık tekrarsız işaretlenip her yıl yeni satır olarak eklenir.
        </v-alert>

        <v-data-table
          :headers="headers"
          :items="holidays"
          :loading="loading"
          hide-actions
          class="elevation-1"
        >
          <template v-slot:items="props">
            <tr>
              <td>{{ props.item.name }}</td>
              <td class="text-no-wrap">{{ formatDate(props.item.startDate) }}</td>
              <td class="text-no-wrap">{{ formatDate(props.item.endDate) }}</td>
              <td>
                <v-chip v-if="props.item.isRecurringYearly" small color="green" text-color="white">
                  Yıllık (her yıl)
                </v-chip>
                <v-chip v-else small color="orange" text-color="white">
                  Tek seferlik
                </v-chip>
              </td>
              <td>
                <v-btn small icon @click="openEdit(props.item)">
                  <v-icon small>edit</v-icon>
                </v-btn>
                <v-btn small icon color="error" @click="openDelete(props.item)">
                  <v-icon small>delete</v-icon>
                </v-btn>
              </td>
            </tr>
          </template>
          <template v-slot:no-data>
            <v-alert :value="true" color="info" icon="info">
              Tatil tanımlı değil.
            </v-alert>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog v-model="formDialog" persistent max-width="520">
      <v-card>
        <v-card-title class="amber darken-3 white--text">
          <v-icon left color="white">event_busy</v-icon>
          {{ form.id ? 'Tatil Düzenle' : 'Yeni Tatil Ekle' }}
        </v-card-title>
        <v-card-text class="pa-4">
          <v-text-field
            v-model="form.name"
            label="Tatil Adı"
            prepend-icon="label"
            :rules="[v => !!v && v.trim().length > 0 || 'Ad zorunludur']"
            maxlength="100"
            counter="100"
            required
            dense
          />

          <v-menu
            v-model="startDateMenu"
            :close-on-content-click="false"
            lazy
            transition="scale-transition"
            offset-y
            full-width
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                :value="form.startDate"
                label="Başlangıç Tarihi"
                prepend-icon="event"
                readonly
                v-on="on"
                dense
              />
            </template>
            <v-date-picker
              v-model="form.startDate"
              locale="tr-TR"
              @input="onStartDateInput"
            />
          </v-menu>

          <v-menu
            v-model="endDateMenu"
            :close-on-content-click="false"
            :disabled="form.isRecurringYearly"
            lazy
            transition="scale-transition"
            offset-y
            full-width
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                :value="form.endDate"
                :label="form.isRecurringYearly ? 'Bitiş Tarihi (yıllık tekrarda kullanılmaz)' : 'Bitiş Tarihi'"
                prepend-icon="event"
                readonly
                :disabled="form.isRecurringYearly"
                v-on="on"
                dense
              />
            </template>
            <v-date-picker
              v-model="form.endDate"
              locale="tr-TR"
              :min="form.startDate"
              @input="endDateMenu = false"
            />
          </v-menu>

          <v-checkbox
            v-model="form.isRecurringYearly"
            label="Her yıl tekrarlar (milli bayram)"
            color="amber darken-3"
            hide-details
            dense
            @change="onRecurringChange"
          />
          <div class="caption grey--text text--darken-1 ml-4 mb-2">
            <span v-if="form.isRecurringYearly">
              ✓ Yıllık tekrarlı: sadece Ay/Gün dikkate alınır. Yıl bilgisi yok sayılır.
            </span>
            <span v-else>
              Tek seferlik: tam tarih aralığı geçerli. Her yıl için ayrı kayıt eklenmelidir.
            </span>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn flat @click="formDialog = false" :disabled="saving">İptal</v-btn>
          <v-btn color="success" :loading="saving" :disabled="!formValid" @click="submitForm">
            {{ form.id ? 'Güncelle' : 'Kaydet' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" persistent max-width="420">
      <v-card v-if="deleteTarget">
        <v-card-title class="error white--text">
          <v-icon left color="white">delete</v-icon>
          Tatil Sil
        </v-card-title>
        <v-card-text class="pa-4">
          <div class="body-2">
            <strong>{{ deleteTarget.name }}</strong> tatilini silmek üzeresiniz. Devam etmek istiyor musunuz?
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn flat @click="deleteDialog = false" :disabled="processing">Vazgeç</v-btn>
          <v-btn color="error" :loading="processing" @click="submitDelete">Sil</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="4000" top>
      {{ snackbarMessage }}
      <v-btn flat @click="snackbar = false">Kapat</v-btn>
    </v-snackbar>
  </v-container>
</template>

<script>
export default {
  name: 'AdminBankHolidays',
  data() {
    return {
      loading: false,
      formDialog: false,
      saving: false,
      startDateMenu: false,
      endDateMenu: false,
      form: {
        id: null,
        name: '',
        startDate: null,
        endDate: null,
        isRecurringYearly: false,
      },

      deleteDialog: false,
      deleteTarget: null,
      processing: false,

      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',

      headers: [
        { text: 'Tatil Adı', value: 'name', sortable: false },
        { text: 'Başlangıç', value: 'startDate', sortable: false },
        { text: 'Bitiş', value: 'endDate', sortable: false },
        { text: 'Tip', value: 'isRecurringYearly', sortable: false },
        { text: 'İşlem', value: 'action', sortable: false },
      ],
    };
  },
  computed: {
    holidays() {
      return this.$store.getters['bankHolidays/holidays'];
    },
    formValid() {
      return (
        !!this.form.name && this.form.name.trim().length > 0 &&
        !!this.form.startDate &&
        !!this.form.endDate &&
        this.form.endDate >= this.form.startDate
      );
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        await this.$store.dispatch('bankHolidays/getHolidays');
      } catch (e) {
        this.showError(e);
      } finally {
        this.loading = false;
      }
    },

    openCreate() {
      const today = this.toIsoDate(new Date());
      this.form = {
        id: null,
        name: '',
        startDate: today,
        endDate: today,
        isRecurringYearly: false,
      };
      this.formDialog = true;
    },
    openEdit(holiday) {
      this.form = {
        id: holiday.id,
        name: holiday.name,
        startDate: this.toIsoDate(holiday.startDate),
        endDate: this.toIsoDate(holiday.endDate),
        isRecurringYearly: holiday.isRecurringYearly,
      };
      this.formDialog = true;
    },
    async submitForm() {
      if (!this.formValid) return;
      this.saving = true;
      try {
        const payload = {
          id: this.form.id,
          name: this.form.name.trim(),
          startDate: this.form.startDate,
          endDate: this.form.endDate,
          isRecurringYearly: this.form.isRecurringYearly,
        };
        if (this.form.id) {
          await this.$store.dispatch('bankHolidays/updateHoliday', payload);
          this.showSuccess('Tatil güncellendi');
        } else {
          await this.$store.dispatch('bankHolidays/createHoliday', payload);
          this.showSuccess('Tatil eklendi');
        }
        this.formDialog = false;
      } catch (e) {
        this.showError(e);
      } finally {
        this.saving = false;
      }
    },

    openDelete(holiday) {
      this.deleteTarget = holiday;
      this.deleteDialog = true;
    },
    async submitDelete() {
      if (!this.deleteTarget) return;
      this.processing = true;
      try {
        await this.$store.dispatch('bankHolidays/deleteHoliday', this.deleteTarget.id);
        this.showSuccess('Tatil silindi');
        this.deleteDialog = false;
      } catch (e) {
        this.showError(e);
      } finally {
        this.processing = false;
      }
    },

    onStartDateInput() {
      this.startDateMenu = false;
      if (this.form.isRecurringYearly) {
        this.form.endDate = this.form.startDate;
        return;
      }
      if (this.form.endDate && this.form.endDate < this.form.startDate) {
        this.form.endDate = this.form.startDate;
      }
      if (!this.form.endDate) {
        this.form.endDate = this.form.startDate;
      }
    },
    onRecurringChange(value) {
      if (value) {
        this.form.endDate = this.form.startDate;
      }
    },

    toIsoDate(d) {
      if (!d) return null;
      const dt = new Date(d);
      if (Number.isNaN(dt.getTime())) return null;
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, '0');
      const day = String(dt.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },
    formatDate(v) {
      if (!v) return '—';
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return '—';
      return d.toLocaleDateString('tr-TR');
    },

    showSuccess(msg) {
      this.snackbarMessage = msg;
      this.snackbarColor = 'success';
      this.snackbar = true;
    },
    showError(e) {
      const msg = (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || 'Bir hata oluştu';
      this.snackbarMessage = msg;
      this.snackbarColor = 'error';
      this.snackbar = true;
    },
  },
};
</script>
